import { useSnackbar } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import AllInclusive from '@mui/icons-material/AllInclusive';
import { Divider } from '@mui/material';
import startCase from 'lodash/startCase';
import React, { useCallback } from 'react';

import { SelectionPanelBase } from './SelectionPanelBase';
import { SelectionPanelItem } from './SelectionPanelItem';
import { thunkCreateTask } from '../../../features/actions';
import { closeSelectionPanel } from '../../../features/ui/uiSlice';
import {
  selectDataExportOutputConnectorsById,
  selectTasksFromWorkcellInstruments,
} from '../../../features/workflow/selectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { PropertiesType, type TaskType } from '../../../types';
import { Icon as MiscIcon } from '../../customIcons/misc';
import { Icon as TasksIcon } from '../../customIcons/tasks';

export const ConnectedTaskSelectionPanel = () => {
  const dispatch = useAppDispatch();
  const isDataExportEnabled = useFlag('fe.canvas_data_export_task');
  const isContinuousLoadingEnabled = useFlag('fe.v2_continuous_loading');
  const tasksInWorkcell = useAppSelector(selectTasksFromWorkcellInstruments);
  const connectors = useAppSelector(selectDataExportOutputConnectorsById);
  const { enqueueSnackbar } = useSnackbar();

  const handleClickItem = useCallback(
    (taskType: TaskType) => {
      dispatch(thunkCreateTask({ taskType }, enqueueSnackbar));
      // TODO(IL, 2024-03-22): have UI reducer do this itself
      dispatch(closeSelectionPanel());
    },
    [dispatch, enqueueSnackbar]
  );

  return (
    <SelectionPanelBase title="Tasks">
      {tasksInWorkcell.map((taskType) => (
        <SelectionPanelItem
          key={taskType}
          itemId={taskType}
          icon={<TasksIcon iconType={taskType} fontSize="medium" />}
          label={startCase(taskType)}
          handleClickItem={handleClickItem}
        />
      ))}
      {isContinuousLoadingEnabled && (
        <>
          <Divider />
          <SelectionPanelItem
            itemId={PropertiesType.ContinuousLoading}
            icon={<AllInclusive fontSize="medium" />}
            label="Continuous Loading"
            handleClickItem={handleClickItem}
          />
        </>
      )}
      <Divider />
      <SelectionPanelItem
        itemId="processdata"
        icon={<MiscIcon iconType="processdata" />}
        label="Process Data"
        handleClickItem={handleClickItem}
      />
      {isDataExportEnabled && connectors.length > 0 && (
        <SelectionPanelItem
          itemId="exportdata"
          icon={<MiscIcon iconType="processdata" />}
          label="Export Data"
          handleClickItem={handleClickItem}
        />
      )}
    </SelectionPanelBase>
  );
};
